import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import PasswordForm from '@sayyesschool/frontend/components/password-form';
import { useManager } from '@sayyesschool/frontend/features/managers';
import { UserDomainLabel } from '@sayyesschool/frontend/features/user';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { Chip, Flex, Grid } from '@sayyesschool/frontend/ui-components';

import ManagerDetails from '@/components/managers/manager-details';
import ManagerEnrollments from '@/components/managers/manager-enrollments';
import ManagerForm from '@/components/managers/manager-form';
import ManagerRequests from '@/components/managers/manager-requests';

export default function ManagerPage() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [manager, actions] = useManager(id);

    const [isManagerFormOpen, toggleManagerFormOpen] = useBoolean(false);
    const [isPasswordDialogOpen, togglePasswordDialogOpen] = useBoolean(false);
    const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] = useBoolean(false);

    useEffect(() => {
        if (location.state?.edit) {
            toggleManagerFormOpen(true);
        } else if (location.state?.delete) {
            toggleConfirmationDialogOpen(true);
        }
    }, []);

    const updateManager = useCallback(data => {
        return actions.updateManager(manager.id, data)
            .then(() => toggleManagerFormOpen(false));
    }, [manager, actions]);

    const updatePassword = useCallback(password => {
        return actions.updateManager(manager.id, { password })
            .finally(() => togglePasswordDialogOpen(false));
    }, [manager, actions]);

    const deleteManager = useCallback(() => {
        return actions.deleteManager(manager.id)
            .then(() => navigate('/managers'));
    }, [manager, actions, navigate]);

    if (!manager) return <LoadingIndicator />;

    return (
        <Page className="ManagerPage">
            <Page.Header
                breadcrumbs={[
                    { key: 'managers', content: 'Менеджеры', to: '/managers' }
                ]}
                title={manager.fullname}
                description={
                    <Chip.Group
                        chips={manager.domains.map(domain => ({
                            key: domain,
                            content: UserDomainLabel[domain]
                        }))}
                        variant="plain"
                    />
                }
                actions={[
                    {
                        key: 'password',
                        title: 'Изменить пароль',
                        icon: 'password',
                        onClick: togglePasswordDialogOpen
                    },
                    {
                        key: 'edit',
                        title: 'Редактировать',
                        icon: 'edit',
                        onClick: toggleManagerFormOpen
                    },
                    {
                        key: 'delete',
                        title: 'Удалить',
                        icon: 'delete',
                        onClick: toggleConfirmationDialogOpen
                    }
                ]}
            />

            <Page.Content>
                <Grid>
                    <Grid.Item
                        xs={12}
                        sm={4}
                        lg={3}
                    >
                        <Flex
                            gap="medium"
                            column
                        >
                            <ManagerDetails
                                manager={manager}
                                onEdit={toggleManagerFormOpen}
                            />
                        </Flex>
                    </Grid.Item>

                    <Grid.Item
                        xs={12}
                        sm={8}
                        lg={9}
                    >
                        <Flex
                            gap="medium"
                            column
                        >
                            <ManagerRequests requests={manager.requests} />
                            <ManagerEnrollments manager={manager} />
                        </Flex>
                    </Grid.Item>
                </Grid>
            </Page.Content>

            <FormDialog
                form="manager-form"
                title="Данные менеджера"
                open={isManagerFormOpen}
                modal
                onClose={toggleManagerFormOpen}
            >
                <ManagerForm
                    id="manager-form"
                    manager={manager}
                    onSubmit={updateManager}
                />
            </FormDialog>

            <FormDialog
                form="password-form"
                title="Изменение пароля"
                open={isPasswordDialogOpen}
                onClose={togglePasswordDialogOpen}
            >
                <PasswordForm
                    id="password-form"
                    onSubmit={updatePassword}
                />
            </FormDialog>

            <ConfirmationDialog
                title="Подтвердите действие"
                message="Вы действительно хотите удалить менеджера?"
                open={isConfirmationDialogOpen}
                onConfirm={deleteManager}
                onClose={toggleConfirmationDialogOpen}
            />
        </Page>
    );
}